// loyalty-phone.js

const LOYALTY_SELECTORS = {
  NEXT_BUTTON: '[data-loyalty-next-button]',
  REDIRECT_URL: '[data-loyalty-redirect]',
  ERROR_MESSAGE: '[data-loyalty-error]',
  QUESTION_INDICATOR: '[data-loyalty-question-indicator]',
};

class LoyaltyPhone extends HTMLElement {
  constructor() {
    super();
    this.viewModel = null;
    this.state = {
      selectedPhone: null,
      selectedConsent: false,
      isSubmitting: false,
      errors: {},
    };
    this._boundHandlers = [];
  }

  connectedCallback() {
    this.viewModel = this.getViewModel();
    this.attachEvents();
    this.updateUI();
  }

  disconnectedCallback() {
    this.cleanupEventListeners();
  }

  getViewModel() {
    return {
      $phoneInput: this.querySelector('#phone'),
      $consentInput: this.querySelector('#newsletter'),
      $nextButton: this.querySelector(LOYALTY_SELECTORS.NEXT_BUTTON),
      $redirectUrl: this.querySelector(LOYALTY_SELECTORS.REDIRECT_URL),
      $errorMessage: this.querySelector(LOYALTY_SELECTORS.ERROR_MESSAGE),
      $questionIndicator: this.querySelector(LOYALTY_SELECTORS.QUESTION_INDICATOR),
      $phoneInputMessages: this.querySelector('#phone').closest('.form-field').querySelector('.form-field__messages'),
    };
  }

  cleanupEventListeners() {
    this._boundHandlers.forEach(({ el, type, fn }) => {
      if (el && typeof el.removeEventListener === 'function') {
        el.removeEventListener(type, fn);
      }
    });
    this._boundHandlers = [];
  }

  addEventHandler(element, type, handler) {
    if (element) {
      element.addEventListener(type, handler);
      this._boundHandlers.push({ el: element, type, fn: handler });
    }
  }

  attachEvents() {
    this.addEventHandler(this.viewModel.$phoneInput, 'change', (e) => this.handlePhoneChange(e));
    this.addEventHandler(this.viewModel.$consentInput, 'change', (e) => this.handleConsentChange(e));
    this.addEventHandler(this.viewModel.$phoneInput, 'blur', (e) => this.validateForm(e));

    this.addEventHandler(this.viewModel.$nextButton, 'click', () => this.handleSubmit());

    this.updateUI();
  }

  handlePhoneChange(e) {
    this.state.selectedPhone = e.target.value;
    this.updateUI();
  }

  handleConsentChange(e) {
    this.state.selectedConsent = e.target.checked;
    this.updateUI();
  }

  async handleSubmit() {
    if (this.state.isSubmitting) return;

    if (this.validateForm()) {
      await this.submitLoyaltyForm();
    }
  }

  validateForm() {
    const errors = {};

    if (!this.state.selectedPhone) {
      errors.phone = 'Please enter your phone number';
    }

    if (this.state.selectedPhone) {
      const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
      if (!phoneRegex.test(this.state.selectedPhone)) {
        errors.phone = 'Please enter a valid phone number';
      } else {
        this.state.selectedPhone = this.state.selectedPhone.replace(phoneRegex, '($1) $2-$3');
      }
    }

    this.state.errors = errors;
    this.updateUI();

    return Object.keys(errors).length === 0;
  }

  clearError(field) {
    if (this.state.errors[field]) {
      delete this.state.errors[field];
      this.updateUI();
    }
  }

  updateUI() {
    this.updateButton();
    this.updateErrors();
    this.updateFieldsStates();
  }

  updateFieldsStates() {
    // Add error state to dropdown containers
    const phoneInput = this.viewModel.$phoneInput;

    if (phoneInput) {
      if (this.state.errors.phone) {
        phoneInput.classList.add('input__error');
      } else {
        phoneInput.classList.remove('input__error');
      }
    }
  }

  updateButton() {
    if (this.viewModel.$nextButton) {
      const canSubmit = this.canSubmit();
      this.viewModel.$nextButton.disabled = !canSubmit || this.state.isSubmitting;
      this.viewModel.$nextButton.setAttribute('aria-disabled', (!canSubmit || this.state.isSubmitting).toString());

      if (this.state.isSubmitting) {
        this.viewModel.$nextButton.classList.add('is-loading');
      } else {
        this.viewModel.$nextButton.classList.remove('is-loading');
      }
    }
  }

  updateErrors() {
    // Update error message in the existing error container or create inline messages
    if (this.viewModel.$errorMessage) {
      const errorMessages = Object.values(this.state.errors);
      if (errorMessages.length > 0) {
        this.viewModel.$errorMessage.textContent = errorMessages[0];
        this.viewModel.$errorMessage.hidden = false;
        this.viewModel.$errorMessage.setAttribute('role', 'alert');
      } else {
        this.viewModel.$errorMessage.hidden = true;
        this.viewModel.$errorMessage.removeAttribute('role');
      }
    } else {
      // Display errors in dropdown message areas
      this.updateFieldsMessages();
    }
  }

  updateFieldsMessages() {
    if (this.viewModel.$phoneInputMessages) {
      if (this.state.errors.phone) {
        this.viewModel.$phoneInputMessages.innerHTML = `<span class="fixed-utility-s">${this.state.errors.phone}</span>`;
      } else {
        this.viewModel.$phoneInputMessages.innerHTML = '';
      }
    }

    if (this.viewModel.$consentInputMessages) {
      if (this.state.errors.consent) {
        this.viewModel.$consentInputMessages.innerHTML = `<span class="fixed-utility-s">${this.state.errors.consent}</span>`;
      } else {
        this.viewModel.$consentInputMessages.innerHTML = '';
      }
    }
  }

  canSubmit() {
    return this.state.selectedPhone;
  }

  async submitLoyaltyForm() {
    this.state.isSubmitting = true;
    this.updateUI();

    const payload = {
      phone: this.state.selectedPhone,
      marketingConsent: this.state.selectedConsent,
    };

    try {
      const response = await fetch('/apps/loyalty-middleware/customer-add-phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Server error (${response.status})`;

        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Use default error message if response isn't JSON
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Birthday submitted successfully:', result);

      this.handleSubmissionSuccess();
    } catch (error) {
      console.error('Failed to submit birthday:', error);
      this.handleSubmissionError(error);
    } finally {
      this.state.isSubmitting = false;
      this.updateUI();
    }
  }

  handleSubmissionSuccess() {
    const redirectUrl = this.getRedirectUrl();

    // Small delay to show success state before redirect
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 500);
  }

  handleSubmissionError(error) {
    this.state.errors.submission = error.message || 'Something went wrong. Please try again.';
    this.updateUI();
  }

  getRedirectUrl() {
    const baseUrl = this.viewModel.$redirectUrl?.getAttribute('data-loyalty-redirect-url') || '/';
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('onboarding_success', 'true');
    return url.toString();
  }
}

// Define the custom element
customElements.define('loyalty-phone', LoyaltyPhone);
